import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2 } from 'lucide-react';

const VACIO = { nombre: '', asesor_id: '', tipo: 'casa', operacion: 'venta',
  estado_inmueble: 'disponible', precio: '', estado: '', ciudad: '', municipio: '', colonia: '',
  calle: '', numero_exterior: '', codigo_postal: '', recamaras: '', banos: '',
  estacionamientos: '', superficie_total: '', superficie_construida: '', anio_construccion: '',
  descripcion: '' };

const num = (v) => (v === '' || v == null ? null : Number(v));

// P3L — Captura y edición territorial de propiedad: sólo datos estructurados, sin imágenes.
export const PropiedadForm = ({ propiedad, asesores, seccionActual, onCerrar, onGuardado }) => {
  const { toast } = useToast();
  const edicion = Boolean(propiedad);
  const [cat, setCat] = useState({ tipos: [], operaciones: [], estados_inmueble: [],
    estados_republica: [] });
  const [f, setF] = useState(VACIO);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    crmTerritorialAPI.catalogosPropiedad().then(setCat).catch(() => {});
    if (edicion) {
      crmTerritorialAPI.propiedadDetalle(propiedad.propiedad_id).then((d) => {
        const lleno = { ...VACIO };
        Object.keys(VACIO).forEach((k) => { lleno[k] = d[k] ?? ''; });
        setF(lleno);
      }).catch((e) => toast({ title: 'No se pudo cargar el inmueble',
        description: e.message, variant: 'destructive' }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const disponibles = seccionActual
    ? asesores.filter((a) => a.seccion_id === seccionActual) : asesores;
  const listo = f.nombre.trim().length >= 5 && Number(f.precio) > 0 && f.estado
    && (edicion || f.asesor_id);

  const guardar = async () => {
    setEnviando(true);
    try {
      const base = {
        nombre: f.nombre.trim(), tipo: f.tipo, operacion: f.operacion,
        precio: Number(f.precio), estado: f.estado, ciudad: f.ciudad.trim() || null,
        municipio: f.municipio.trim() || null, colonia: f.colonia.trim() || null,
        calle: f.calle.trim() || null, numero_exterior: f.numero_exterior.trim() || null,
        codigo_postal: f.codigo_postal.trim() || null, recamaras: num(f.recamaras),
        banos: num(f.banos), estacionamientos: num(f.estacionamientos),
        superficie_total: num(f.superficie_total),
        superficie_construida: num(f.superficie_construida),
        anio_construccion: num(f.anio_construccion),
        descripcion: f.descripcion.trim() || null,
      };
      const r = edicion
        ? await crmTerritorialAPI.actualizarPropiedad(propiedad.propiedad_id,
            { ...base, estado_inmueble: f.estado_inmueble })
        : await crmTerritorialAPI.crearPropiedad({ ...base, asesor_id: f.asesor_id });
      toast({ title: edicion ? 'Propiedad actualizada' : 'Propiedad capturada',
        description: `${r.nombre} (${r.referencia}) en ${r.seccion}.` });
      onGuardado();
      onCerrar();
    } catch (e) {
      toast({ title: 'Operación rechazada', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  const campo = (k, ph, extra = {}) => (
    <Input value={f[k]} placeholder={ph} data-testid={`propiedad-${k}`}
           onChange={(e) => setF({ ...f, [k]: e.target.value })} {...extra} />
  );
  const selector = (k, opciones, vacio) => (
    <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid={`propiedad-${k}`}>
      {vacio && <option value="">{vacio}</option>}
      {opciones.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  );

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="propiedad-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {edicion ? `Editar propiedad · ${propiedad.referencia || ''}`
                     : 'Nueva propiedad territorial'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {campo('nombre', 'Título del inmueble (mínimo 5 caracteres)')}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {!edicion && (
              <select value={f.asesor_id}
                      onChange={(e) => setF({ ...f, asesor_id: e.target.value })}
                      className="border rounded-lg px-3 py-2 text-sm bg-white"
                      data-testid="propiedad-asesor">
                <option value="">Asesor responsable…</option>
                {disponibles.map((a) => (
                  <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
                ))}
              </select>
            )}
            {selector('tipo', cat.tipos)}
            {selector('operacion', cat.operaciones)}
            {edicion && selector('estado_inmueble', cat.estados_inmueble)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {campo('precio', 'Precio (MXN)', { type: 'number', min: '0' })}
            {selector('estado', cat.estados_republica, 'Estado de la república…')}
            {campo('ciudad', 'Ciudad')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {campo('municipio', 'Municipio / alcaldía')}
            {campo('colonia', 'Colonia')}
            {campo('codigo_postal', 'C.P.')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {campo('calle', 'Calle')}
            {campo('numero_exterior', 'Número exterior')}
            {campo('anio_construccion', 'Año de construcción', { type: 'number' })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {campo('recamaras', 'Recámaras', { type: 'number', min: '0' })}
            {campo('banos', 'Baños', { type: 'number', min: '0', step: '0.5' })}
            {campo('estacionamientos', 'Cajones', { type: 'number', min: '0' })}
            {campo('superficie_total', 'm² terreno', { type: 'number', min: '0' })}
            {campo('superficie_construida', 'm² constr.', { type: 'number', min: '0' })}
          </div>
          {campo('descripcion', 'Descripción del inmueble')}
          <Button onClick={guardar} disabled={!listo || enviando}
                  data-testid="propiedad-guardar-btn">
            {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {edicion ? 'Guardar cambios' : 'Capturar propiedad'}
          </Button>
          <p className="text-xs text-gray-500">
            Sólo datos estructurados: sin imágenes ni archivos en esta fase. El inmueble queda
            dentro de la sección de su asesor, con auditoría {edicion ? 'antes/después de cada '
            + 'campo modificado' : 'del alta y nota en la bitácora'}. Sin envíos externos.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropiedadForm;
